import { useQuery, useQueryClient, useMutation, skipToken } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import useSession from '@hooks/useSession'
import {
  fetchStudentsByParticipationId,
  createStudentForParticipation,
  updateStudentForParticipation,
  deleteStudentForParticipation,
} from '@/services/contribution/api'
import { InvalidSessionError } from '@/util/errors'

import type { Student } from '@/services/contribution/contribution.d.ts'

export interface StudentData {
  id?: string
  firstName: string
  lastName: string
  classroomId: number | null
}

export default function useStudents() {
  const queryClient = useQueryClient()

  const { session } = useSession()

  const {
    data: students,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['students'],
    queryFn: session ? () => fetchStudentsByParticipationId(session.participationId) : skipToken,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!session,
  })

  const createStudentMutation = useMutation({
    mutationFn: ({ student, participationId }: { student: StudentData; participationId: string }) =>
      createStudentForParticipation({
        firstName: student.firstName.trim(),
        lastName: student.lastName.trim(),
        classroomId: student.classroomId,
        participationId: participationId,
      }),
  })

  async function createStudent({ student }: { student: StudentData }) {
    if (!session) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Invalid session',
        level: 'error',
        data: {
          file: 'hooks/createStudent Mutation',
        },
      })
      throw new InvalidSessionError('No session available')
    }

    const newStudent = await createStudentMutation.mutateAsync({
      student,
      participationId: session.participationId,
    })
    queryClient.setQueryData(['students'], (oldData: Student[]) => {
      return [...oldData, newStudent]
    })

    return newStudent
  }

  const updateStudentMutation = useMutation({
    mutationFn: (student: StudentData) =>
      updateStudentForParticipation({
        studentId: student.id?.toString() || '',
        firstName: student.firstName.trim(),
        lastName: student.lastName.trim(),
        classroomId: student.classroomId,
      }),
  })

  async function updateStudent({ student }: { student: StudentData }) {
    if (!student?.id) {
      throw new Error('Student ID is required to update.')
    }

    const updatedStudent = await updateStudentMutation.mutateAsync({
      id: student.id,
      firstName: student.firstName.trim(),
      lastName: student.lastName.trim(),
      classroomId: student.classroomId,
    })

    queryClient.invalidateQueries({ queryKey: ['students'] })
    return updatedStudent
  }

  const deleteStudentMutation = useMutation({
    mutationFn: (studentData: { studentId: string }) => deleteStudentForParticipation(studentData),
  })

  async function deleteStudent(studentData: { studentId: string }) {
    if (!studentData.studentId) {
      throw new Error('Student ID is required to delete.')
    }

    const success = await deleteStudentMutation.mutateAsync(studentData)

    queryClient.invalidateQueries({ queryKey: ['students'] })
    return success
  }

  return {
    students,
    isLoading,
    error,
    createStudent,
    updateStudent,
    deleteStudent,
  }
}
